if (typeof AFRAME === "undefined") {
    throw new Error(
        "Component attempted to register before AFRAME was available."
    );
}
import "./activators";
import "./behaviours";

AFRAME.currentInputMapping = null;
AFRAME.inputMappings = {};
AFRAME.inputActions = {};
AFRAME.activeBehaviours = {};

AFRAME.registerSystem("input-mapping", {
    mappings: {},
    mappingsPerControllers: {},
    loadedControllers: [],

    init: function () {
        const self = this;
        this.keyboardHandler = this.keyboardHandler.bind(this);

        this.sceneEl.addEventListener("inputmappingregistered", () => {
            self.removeControllersListeners();
            for (let i = 0; i < self.loadedControllers.length; i++) {
                const controller = self.loadedControllers[i];
                self.updateControllersListeners(controller);
            }
        });

        this.sceneEl.addEventListener("controllerconnected", (event) => {
            const matchingController = self.findMatchingController(
                event.target
            );
            if (matchingController) {
                self.updateControllersListeners(matchingController);
            } else {
                const newController = {
                    name: event.detail.name,
                    hand: event.detail.component.data.hand,
                    element: event.target,
                    handlers: {},
                    activators: {},
                };
                self.loadedControllers.push(newController);
                self.updateControllersListeners(newController);
            }
        });

        this.sceneEl.addEventListener("controllerdisconnected", (event) => {
            const matchingController = self.findMatchingController(
                event.target
            );
            if (matchingController) {
                self.removeControllerListeners(matchingController);
                self.removeActiveBehaviours(matchingController);
            }
        });

        this.addKeyboardListeners();
    },

    findMatchingController: function (element) {
        for (let i = 0; i < this.loadedControllers.length; i++) {
            const controller = this.loadedControllers[i];
            if (controller.element === element) {
                return controller;
            }
        }
    },

    addKeyboardListeners: function () {
        document.addEventListener("keyup", this.keyboardHandler);
        document.addEventListener("keydown", this.keyboardHandler);
        document.addEventListener("keypress", this.keyboardHandler);
    },

    removeKeyboardListeners: function () {
        document.removeEventListener("keyup", this.keyboardHandler);
        document.removeEventListener("keydown", this.keyboardHandler);
        document.removeEventListener("keypress", this.keyboardHandler);
    },

    removeControllerListeners: function (controller) {
        for (const eventName in controller.handlers) {
            const handler = controller.handlers[eventName];
            controller.element.removeEventListener(eventName, handler);
        }
        controller.handlers = {};

        for (const activatorKey in controller.activators) {
            controller.activators[activatorKey].removeListeners();
        }
        controller.activators = {};
    },

    removeActiveBehaviours: function (controller) {
        const controllerKey = `${controller.name}${
            controller.hand ? "_" + controller.hand : ""
        }`;
        for (const behaviourKey in AFRAME.activeBehaviours[controllerKey]) {
            const behaviour =
                AFRAME.activeBehaviours[controllerKey][behaviourKey];
            if (behaviour.removeEventListeners) {
                behaviour.removeEventListeners();
            }
        }
    },

    updateBehaviours: function (controller) {
        const behaviours =
            this.mappingsPerControllers[controller.name].behaviours;
        if (!behaviours) {
            return;
        }
        const controllerKey = `${controller.name}${
            controller.hand ? "_" + controller.hand : ""
        }`;

        AFRAME.activeBehaviours[controllerKey] = {};
        for (const behaviourKey in behaviours) {
            const behaviourName = behaviours[behaviourKey];
            const inputBehaviour = AFRAME.inputBehaviours[behaviourName];
            if (inputBehaviour) {
                const behaviourInstance = new inputBehaviour(
                    controller.element,
                    behaviourKey
                );
                if (behaviourInstance.addEventListeners) {
                    behaviourInstance.addEventListeners();
                }
                AFRAME.activeBehaviours[controllerKey][behaviourName] =
                    behaviourInstance;
            }
        }
    },

    updateControllersListeners: function (controller) {
        this.removeControllerListeners(controller);
        this.removeActiveBehaviours(controller);

        if (AFRAME.inputMappings) {
            const controllerMappings = (this.mappingsPerControllers[
                controller.name
            ] = {
                mappings: {},
                behaviours: {},
            });

            for (const inputMappingKey in AFRAME.inputMappings.mappings) {
                const inputMapping =
                    AFRAME.inputMappings.mappings[inputMappingKey];
                const commonMapping = inputMapping.common;

                if (commonMapping) {
                    this.updateMappingsPerController(
                        commonMapping,
                        controllerMappings,
                        inputMappingKey
                    );
                }

                const specificMapping = inputMapping[controller.name];
                if (specificMapping) {
                    this.updateMappingsPerController(
                        specificMapping,
                        controllerMappings,
                        inputMappingKey
                    );
                }
            }

            for (const behaviourKey in AFRAME.inputMappings.behaviours) {
                const specificBehaviour =
                    AFRAME.inputMappings.behaviours[behaviourKey][
                        controller.name
                    ];
                if (specificBehaviour) {
                    this.updateBehavioursPerController(
                        specificBehaviour,
                        controllerMappings,
                        behaviourKey
                    );
                }
            }

            const triggerMappedEvents = (event, mappedEvents) => {
                if (
                    typeof mappedEvents !== "object" ||
                    (mappedEvents =
                        mappedEvents[
                            this.findMatchingController(event.target).hand
                        ])
                ) {
                    if (Array.isArray(mappedEvents)) {
                        for (const eventName of mappedEvents) {
                            event.target.emit(eventName, event.detail);
                        }
                    } else {
                        event.target.emit(mappedEvents, event.detail);
                    }
                }
            };

            const generateHandler = (eventName) => {
                return (event) => {
                    const mappedEvents =
                        controllerMappings.mappings[eventName][
                            AFRAME.currentInputMapping
                        ];
                    triggerMappedEvents(event, mappedEvents);
                };
            };

            for (const eventName in controllerMappings.mappings) {
                if (eventName.includes(".")) {
                    const [componentName, activatorName] = eventName.split(".");
                    const handler = generateHandler(eventName);
                    const inputActivator =
                        AFRAME.inputActivators[activatorName];

                    controller.activators[eventName] = new inputActivator(
                        controller.element,
                        componentName,
                        handler
                    );
                }

                const handler = generateHandler(eventName);
                controller.element.addEventListener(eventName, handler);
                controller.handlers[eventName] = handler;
            }

            this.updateBehaviours(controller);
        }
    },

    checkValidInputMapping: function () {
        return AFRAME.currentInputMapping;
    },

    keyboardHandler: function (event) {
        if (
            this.checkValidInputMapping() &&
            AFRAME.inputMappings &&
            AFRAME.inputMappings.mappings[AFRAME.currentInputMapping] &&
            AFRAME.inputMappings.mappings[AFRAME.currentInputMapping].keyboard
        ) {
            const keyName =
                (event.keyCode === 32 ? "Space" : event.code) +
                "_" +
                event.type.substr(3);
            const eventName =
                AFRAME.inputMappings.mappings[AFRAME.currentInputMapping]
                    .keyboard[keyName.toLowerCase()];
            if (eventName) {
                this.sceneEl.emit(eventName);
            }
        }
    },

    updateBehavioursPerController: function (
        behaviours,
        controllerMappings,
        behaviourKey
    ) {
        for (const behaviourName in behaviours) {
            const behaviour = behaviours[behaviourName];
            if (!controllerMappings.behaviours[behaviourName]) {
                controllerMappings.behaviours[behaviourName] = behaviour;
            }
        }
    },

    updateMappingsPerController: function (
        mappings,
        controllerMappings,
        inputMappingKey
    ) {
        for (const eventName in mappings) {
            const mappedEvents = mappings[eventName];
            if (!controllerMappings.mappings[eventName]) {
                controllerMappings.mappings[eventName] = {};
            }
            controllerMappings.mappings[eventName][inputMappingKey] =
                mappedEvents;
        }
    },

    removeControllersListeners: function () {
        for (let i = 0; i < this.loadedControllers.length; i++) {
            const controller = this.loadedControllers[i];
            this.removeControllerListeners(controller);
        }
        this.mappingsPerControllers = {
            mappings: {},
            behaviours: {},
        };
    },
});

AFRAME.registerInputActions = function (actions, defaultInputMapping) {
    AFRAME.inputActions = actions;
    if (
        defaultInputMapping !== undefined &&
        AFRAME.inputActions[defaultInputMapping]
    ) {
        AFRAME.currentInputMapping = defaultInputMapping;
    }
};

AFRAME.registerInputMappings = function (inputMappings, overwrite) {
    if (overwrite || Object.keys(AFRAME.inputMappings).length === 0) {
        AFRAME.inputMappings = inputMappings;
    } else {
        AFRAME.inputMappings.behaviours = inputMappings.behaviours;
        for (const inputMappingKey in inputMappings.mappings) {
            const inputMapping = inputMappings.mappings[inputMappingKey];
            if (AFRAME.inputMappings[inputMappingKey]) {
                for (const controllerName in inputMapping) {
                    const controllerMappings = inputMapping[controllerName];
                    if (AFRAME.inputMappings[inputMappingKey][controllerName]) {
                        for (const eventName in controllerMappings) {
                            AFRAME.inputMappings[inputMappingKey][
                                controllerName
                            ][eventName] = controllerMappings[eventName];
                        }
                    } else {
                        AFRAME.inputMappings[inputMappingKey][controllerName] =
                            controllerMappings;
                    }
                }
            } else {
                AFRAME.inputMappings[inputMappingKey] = inputMapping;
            }
        }
    }

    if (AFRAME.scenes) {
        for (let i = 0; i < AFRAME.scenes.length; i++) {
            AFRAME.scenes[i].emit("inputmappingregistered");
        }
    }
};
